import React, { useContext, useState, useEffect } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import Popup from '../Common/Popup';

/**
 * The `InvestorDealFilter` function is a React component that renders a filter for deal amounts by investor.
 * @returns a JSX element.
 */
function InvestorDealFilter({ customPathname, invDealRange, setInvDealRange, label = "Deal (By Investor - $M)", isPopupOpen, setIsPopupOpen }) {
    const { refineSearchFilters } = useContext(PeFilterContext);
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;
    const [error, setError] = useState("");

    useEffect(() => {
        const from = refineSearchFilters[customPathname]?.dealInvestorRange?.at(0)?.name ?? "";
        const to = refineSearchFilters[customPathname]?.dealInvestorRange?.at(1)?.name ?? "";
        setInvDealRange({ from, to })
    }, [customPathname, refineSearchFilters, setInvDealRange]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        const formatedValue = value.replace(/[^0-9]/g, '')
        setInvDealRange(prev => ({ ...prev, [name]: formatedValue }))
        // Clear error when user starts typing
        if (error) setError("");
    };

    // Validate when one field has value but other is empty
    useEffect(() => {
        const hasFrom = invDealRange?.from !== "" && invDealRange?.from !== null;
        const hasTo = invDealRange?.to !== "" && invDealRange?.to !== null;
        
        if (hasFrom && !hasTo) {
            setError("Please enter To value");
        } else if (!hasFrom && hasTo) {
            setError("Please enter From value");
        } else if (hasFrom && hasTo && parseFloat(invDealRange.from) > parseFloat(invDealRange.to)) {
            setError("From value must be less than To value");
        } else {
            setError("");
        }
    }, [invDealRange?.from, invDealRange?.to]);

    const getDisplayText = () => {
        if (invDealRange?.from && invDealRange?.to) {
            return `: ${invDealRange.from} to ${invDealRange.to}`;
        } else if (invDealRange?.from) {
            return `: From ${invDealRange.from}`;
        } else if (invDealRange?.to) {
            return `: Up to ${invDealRange.to}`;
        }
        return " ";
    };

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100  text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {(invDealRange?.from || invDealRange?.to) && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {getDisplayText()}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    {/* <div className="p-4 min-w-[300px]"> */}
                    <div className="p-4 min-w-[225px] md:min-w-[300px]">
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                From ($M)
                            </label>
                            <input 
                                type="number" 
                                min={0} 
                                name="from" 
                                onChange={handleChange}
                                className={`w-full p-2 border rounded focus:outline-none bg-white ${
                                    error && (!invDealRange?.from || parseFloat(invDealRange.from) > parseFloat(invDealRange.to))
                                        ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                                        : 'border-[#BC8B20]'
                                }`}
                                value={invDealRange?.from ?? ""} 
                                placeholder="0" 
                                autoComplete='off' 
                            />
                        </div>
                        <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                To ($M)
                            </label>
                            <input 
                                type="number" 
                                min={invDealRange?.from || 0} 
                                name="to" 
                                onChange={handleChange}
                                className={`w-full p-2 border rounded focus:outline-none bg-white ${
                                    error && (!invDealRange?.to || parseFloat(invDealRange.from) > parseFloat(invDealRange.to))
                                        ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                                        : 'border-[#BC8B20]'
                                }`}
                                value={invDealRange?.to ?? ""} 
                                placeholder="" 
                                autoComplete='off' 
                            />
                        </div>
                        {error && (
                            <p className="text-red-500 text-sm mt-2">{error}</p>
                        )}
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default InvestorDealFilter;